import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Calendar, Users, MapPin, CreditCard, User, Mail, Phone, AlertCircle, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, login } = useAuth();
  
  const [booking, setBooking] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(true);
  const [showAccountSection, setShowAccountSection] = useState(false);
  const [accountMode, setAccountMode] = useState('register'); // 'register' or 'login'
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });

  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');

  useEffect(() => {
    const fetchBookingAndStatus = async () => {
      if (!bookingId || !sessionId) {
        toast.error('Invalid booking confirmation link');
        navigate('/packages');
        return;
      }

      try {
        // Fetch booking details
        const bookingResponse = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(bookingResponse.data);
        
        // Pre-fill account form with booking details
        setAccountForm(prev => ({
          ...prev,
          email: bookingResponse.data.customer_email,
          name: bookingResponse.data.customer_name,
          phone: bookingResponse.data.customer_phone
        }));
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching booking:', error);
        toast.error('Could not load booking details');
        setLoading(false);
      }
    };

    fetchBookingAndStatus();
  }, [bookingId, sessionId, navigate]);

  // Poll for payment status
  useEffect(() => {
    if (!sessionId || !polling) return;

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentStatus(response.data);
        
        if (response.data.payment_status === 'paid') {
          setPolling(false);
          // Refresh booking to get updated status
          if (bookingId) {
            const bookingResponse = await axios.get(`${API}/bookings/${bookingId}`);
            setBooking(bookingResponse.data);
          }
        } else if (response.data.status === 'expired') {
          setPolling(false);
          toast.error('Payment session expired');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    pollStatus();
    const interval = setInterval(pollStatus, 2000);
    
    // Stop polling after 30 seconds
    const timeout = setTimeout(() => {
      setPolling(false);
      clearInterval(interval);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [sessionId, bookingId, polling]);

  const handleAccountFormChange = (e) => {
    setAccountForm({ ...accountForm, [e.target.name]: e.target.value });
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountLoading(true);

    try {
      // Register customer
      const response = await axios.post(`${API}/customer/register`, accountForm);
      localStorage.setItem('token', response.data.access_token);
      
      // Link booking to account
      await axios.post(`${API}/bookings/${bookingId}/link-account`, {}, {
        headers: { Authorization: `Bearer ${response.data.access_token}` }
      });
      
      toast.success('Account created and booking saved!');
      setShowAccountSection(false);
      
      // Reload page to update auth state
      window.location.reload();
    } catch (error) {
      console.error('Error creating account:', error);
      if (error.response?.data?.detail === 'Email already registered') {
        toast.error('Email already registered. Please log in instead.');
        setAccountMode('login');
      } else {
        toast.error('Failed to create account. Please try again.');
      }
    } finally {
      setAccountLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAccountLoading(true);

    try {
      await login(accountForm.email, accountForm.password);
      
      // Link booking to account
      const token = localStorage.getItem('token');
      await axios.post(`${API}/bookings/${bookingId}/link-account`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Logged in and booking saved to your account!');
      setShowAccountSection(false);
    } catch (error) {
      console.error('Error logging in:', error);
      toast.error('Invalid email or password');
    } finally {
      setAccountLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon mx-auto mb-4"></div>
          <p className="text-maroon text-xl">Loading your booking...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-terracotta mx-auto mb-4" />
          <p className="text-maroon text-xl mb-4">Booking not found</p>
          <Link to="/packages" className="btn-primary">Browse Packages</Link>
        </div>
      </div>
    );
  }

  const isPaid = paymentStatus?.payment_status === 'paid' || booking.payment_status === 'paid';

  return (
    <div className="py-16" data-testid="booking-confirmation-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Success Header */}
        <div className="text-center mb-12">
          {isPaid ? (
            <>
              <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
              <h1 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-4">
                Booking Confirmed!
              </h1>
              <p className="text-xl text-maroon/70">
                Thank you for booking with Chimba Travel. Your adventure awaits!
              </p>
            </>
          ) : polling ? (
            <>
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-maroon mx-auto mb-4"></div>
              <h1 className="text-4xl font-heading font-bold text-maroon mb-4">
                Processing Payment...
              </h1>
              <p className="text-xl text-maroon/70">
                Please wait while we confirm your payment.
              </p>
            </>
          ) : (
            <>
              <AlertCircle size={64} className="text-yellow-600 mx-auto mb-4" />
              <h1 className="text-4xl font-heading font-bold text-maroon mb-4">
                Payment Pending
              </h1>
              <p className="text-xl text-maroon/70">
                We're still waiting for payment confirmation. Please check your email for updates.
              </p>
            </>
          )}
        </div>

        {/* Booking Reference */}
        <div className="bg-maroon text-white p-6 rounded-sm mb-8 text-center">
          <p className="text-sm opacity-80 mb-1">Booking Reference</p>
          <p className="text-3xl font-heading font-bold tracking-wider" data-testid="booking-reference">
            {booking.id.slice(0, 8).toUpperCase()}
          </p>
        </div>

        {/* Booking Summary */}
        <div className="card mb-8">
          <h2 className="text-2xl font-heading font-bold text-maroon mb-6">Booking Summary</h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-4 pb-4 border-b border-maroon/10">
              <MapPin className="text-terracotta flex-shrink-0 mt-1" size={20} />
              <div>
                <p className="font-semibold text-maroon">{booking.package_title}</p>
                <p className="text-sm text-maroon/60">Travel Package</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="text-terracotta" size={20} />
                <div>
                  <p className="text-sm text-maroon/60">Preferred Dates</p>
                  <p className="font-semibold text-maroon">{booking.preferred_dates}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Users className="text-terracotta" size={20} />
                <div>
                  <p className="text-sm text-maroon/60">Travelers</p>
                  <p className="font-semibold text-maroon">{booking.group_size} {booking.group_size === 1 ? 'person' : 'people'}</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-maroon/10">
              <div className="flex justify-between items-center mb-2">
                <span className="text-maroon/70">Package Price (per person)</span>
                <span className="text-maroon">${booking.package_price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center text-lg font-bold">
                <span className="text-maroon">Deposit Paid (5% non-refundable)</span>
                <span className="text-terracotta">${booking.deposit_amount.toFixed(2)}</span>
              </div>
              <div className="text-right mt-1">
                <Link to="/terms#refund-policy" className="text-xs text-maroon/60 hover:text-terracotta underline">
                  View refund policy
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="card mb-8">
          <h2 className="text-2xl font-heading font-bold text-maroon mb-6">Contact Details</h2>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="text-terracotta" size={20} />
              <span className="text-maroon">{booking.customer_name}</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="text-terracotta" size={20} />
              <span className="text-maroon">{booking.customer_email}</span>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="text-terracotta" size={20} />
              <span className="text-maroon">{booking.customer_phone}</span>
            </div>
          </div>
        </div>

        {/* Save to Account Section */}
        {isPaid && !user && !booking.user_id && (
          <div className="card mb-8 border-2 border-terracotta">
            <div className="flex items-start gap-4">
              <CreditCard className="text-terracotta flex-shrink-0" size={24} />
              <div className="flex-1">
                <h3 className="text-xl font-heading font-bold text-maroon mb-2">Save Your Booking</h3>
                <p className="text-maroon/70 mb-4">
                  Create an account to save this booking and easily manage future reservations.
                </p>
                
                {!showAccountSection ? (
                  <Button 
                    onClick={() => setShowAccountSection(true)}
                    className="btn-primary"
                    data-testid="create-account-btn"
                  >
                    Create Account
                  </Button>
                ) : (
                  <div className="mt-4">
                    <div className="flex gap-4 mb-4">
                      <Button
                        type="button"
                        variant={accountMode === 'register' ? 'default' : 'outline'}
                        onClick={() => setAccountMode('register')}
                        className={accountMode === 'register' ? 'btn-primary' : ''}
                      >
                        New Account
                      </Button>
                      <Button
                        type="button"
                        variant={accountMode === 'login' ? 'default' : 'outline'}
                        onClick={() => setAccountMode('login')}
                        className={accountMode === 'login' ? 'btn-primary' : ''}
                      >
                        <LogIn size={16} className="mr-2" />
                        Existing Account
                      </Button>
                    </div>

                    {accountMode === 'register' ? (
                      <form onSubmit={handleCreateAccount} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="acc-name">Full Name</Label>
                          <Input
                            id="acc-name"
                            name="name"
                            value={accountForm.name}
                            onChange={handleAccountFormChange}
                            required
                            className="input-field"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="acc-email">Email</Label>
                          <Input
                            id="acc-email"
                            name="email"
                            type="email"
                            value={accountForm.email}
                            onChange={handleAccountFormChange}
                            required
                            className="input-field"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="acc-phone">Phone</Label>
                          <Input
                            id="acc-phone"
                            name="phone"
                            value={accountForm.phone}
                            onChange={handleAccountFormChange}
                            className="input-field"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="acc-password">Create Password</Label>
                          <Input
                            id="acc-password"
                            name="password"
                            type="password"
                            value={accountForm.password}
                            onChange={handleAccountFormChange}
                            required
                            minLength={6}
                            className="input-field"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="btn-primary w-full"
                          disabled={accountLoading}
                        >
                          {accountLoading ? 'Creating Account...' : 'Create Account & Save Booking'}
                        </Button>
                      </form>
                    ) : (
                      <form onSubmit={handleLogin} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="login-email">Email</Label>
                          <Input
                            id="login-email"
                            name="email"
                            type="email"
                            value={accountForm.email}
                            onChange={handleAccountFormChange}
                            required
                            className="input-field"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="login-password">Password</Label>
                          <Input
                            id="login-password"
                            name="password"
                            type="password"
                            value={accountForm.password}
                            onChange={handleAccountFormChange}
                            required
                            className="input-field"
                          />
                        </div>
                        <Button 
                          type="submit" 
                          className="btn-primary w-full"
                          disabled={accountLoading}
                        >
                          {accountLoading ? 'Logging in...' : 'Log In & Save Booking'}
                        </Button>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Account Linked Success */}
        {(user || booking.user_id) && isPaid && (
          <div className="card mb-8 bg-green-50 border-green-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-600" size={24} />
              <p className="text-green-800 font-semibold">Booking saved to your account</p>
            </div>
          </div>
        )}

        {/* Next Steps */}
        {isPaid && (
          <div className="card mb-8 bg-sand/30">
            <h2 className="text-2xl font-heading font-bold text-maroon mb-4">What's Next?</h2>
            <ol className="space-y-3 text-maroon/80">
              <li className="flex gap-3">
                <span className="font-bold text-terracotta">1.</span>
                <span>You'll receive a confirmation email with all booking details.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-terracotta">2.</span>
                <span>Our travel specialists will contact you within 24-48 hours.</span>
              </li>
              <li className="flex gap-3">
                <span className="font-bold text-terracotta">3.</span>
                <span>We'll finalize your travel plans and discuss payment for the remaining balance.</span>
              </li>
            </ol>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/packages" className="btn-primary text-center">
            Explore More Packages
          </Link>
          <Link to="/" className="btn-secondary text-center">
            Return Home
          </Link>
        </div>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-maroon/70">
            Questions? Call us at <a href="tel:+18335688090" className="font-bold text-maroon hover:text-terracotta underline">(833) 568-8090</a> or email{' '}
            <a href="mailto:hello@chimbatravel.com" className="font-bold text-maroon hover:text-terracotta underline">hello@chimbatravel.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
